// Veículos do contrato (pedido 2026-10-14: "criar contrato só colocando os
// dados do veículo à mão, sem precisar ter veículo cadastrado; e se tiver
// veículo cadastrado, poder selecionar"). Fica gravado no próprio contrato
// (client_contracts.vehicles). Lista vazia = o documento e a assinatura
// usam os veículos vinculados ao cliente (comportamento antigo).
// Usado na aba CONTRATO (Editar Usuário), no contrato e no pré-cadastro da
// tela Vendas. Cada veículo tem seu plano e sua mensalidade (2026-10-15).
import { useEffect, useState } from "react";
import { Car, Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { vehicleMetadataService } from "@/services/vehicle-metadata.service";
import type { TraccarDeviceRef } from "@/services/vehicle-permissions.service";
import type { ContractVehicle } from "@/services/gpsdrive-client-contract.service";
import type { Plan } from "@/services/gpsdrive-plans.service";
import { AssistancePlanBadge } from "@/components/assistance-plan-badge";

// "Pacote Ouro" / "PLANO OURO antigo" / "OURO" → "OURO" (mesma regra do
// backend, assistance-clients.service.ts#planKey): o plano digitado no
// contrato conta como assistência quando bate com um Plano marcado "com
// assistência" (pedido 2026-10-16).
export function planKey(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/R\$\s*[\d.,]+.*$/, "")
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\b(PACOTE|PLANO|ANTIGO|NOVO)\b/g, " ")
    .replace(/\b\d+\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
export function planHasAssistance(name: string, plans?: Plan[]): boolean {
  const k = planKey(name);
  return !!k && !!plans?.some((p) => p.serviceGroupId && planKey(p.name) === k);
}

const blank = (plan = "", monthlyValue: number | null = null): ContractVehicle => ({
  plate: "",
  model: "",
  year: "",
  color: "",
  plan,
  monthlyValue,
});

const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Soma das mensalidades dos veículos (null se nenhum tem valor). */
export function vehiclesMonthlyTotal(vehicles: { monthlyValue?: number | null }[]): number | null {
  const values = vehicles.map((v) => v.monthlyValue).filter((n): n is number => typeof n === "number");
  return values.length ? Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100 : null;
}

export function ContractVehiclesEditor({
  value,
  onChange,
  registered,
  plans,
  disabled,
  defaultPlan = "",
  defaultMonthlyValue = null,
}: {
  value: ContractVehicle[];
  onChange: (next: ContractVehicle[]) => void;
  /** Veículos já cadastrados e vinculados ao cliente (pode ser vazio). */
  registered: TraccarDeviceRef[];
  plans?: Plan[];
  disabled?: boolean;
  defaultPlan?: string;
  defaultMonthlyValue?: number | null;
}) {
  const [picking, setPicking] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const upd = (i: number, patch: Partial<ContractVehicle>) =>
    onChange(value.map((v, j) => (j === i ? { ...v, ...patch } : v)));

  // Veículo cadastrado: placa do rastreador + marca/modelo/ano/cor/plano do
  // cadastro (Editar dispositivo). Falha no cadastro = só placa/nome.
  const pickRegistered = async (d: TraccarDeviceRef) => {
    setLoadingId(d.id);
    const meta = await vehicleMetadataService.get(d.id).catch(() => null);
    setLoadingId(null);
    setPicking(false);
    const plate = String((d.attributes?.plate as string | undefined) ?? "").trim().toUpperCase();
    const model = [meta?.brand, meta?.model].filter(Boolean).join(" ").toUpperCase() || (plate ? "" : d.name);
    const metaPlan = meta?.planId ? plans?.find((p) => p.id === meta.planId) : undefined;
    const plan = metaPlan?.name || defaultPlan;
    onChange([
      ...value,
      {
        plate: plate || "",
        model,
        year: meta?.year ? String(meta.year) : "",
        color: (meta?.color ?? "").toUpperCase(),
        plan,
        monthlyValue: metaPlan?.value ?? defaultMonthlyValue,
      },
    ]);
  };

  const plateOf = (d: TraccarDeviceRef) => String((d.attributes?.plate as string | undefined) ?? "").toUpperCase();
  const alreadyIn = (d: TraccarDeviceRef) => {
    const p = plateOf(d).replace(/[^A-Z0-9]/g, "");
    return !!p && value.some((v) => v.plate.replace(/[^A-Z0-9]/gi, "").toUpperCase() === p);
  };

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          {registered.length
            ? "Nenhum veículo no contrato — o contrato usa os veículos vinculados ao cliente. Escolha um cadastrado ou digite à mão."
            : "Nenhum veículo — digite os dados do veículo (não precisa estar cadastrado)."}
        </p>
      ) : (
        value.map((v, i) => (
          <div key={i} className="rounded-lg border border-border bg-muted/20 p-2.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Veículo {i + 1}
              </span>
              {!disabled && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => onChange(value.filter((_, j) => j !== i))}
                  aria-label="Remover veículo"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_1.6fr_0.7fr_0.9fr]">
              <LabeledInput label="Placa" value={v.plate} disabled={disabled} onChange={(x) => upd(i, { plate: x.toUpperCase() })} />
              <LabeledInput label="Marca / modelo" value={v.model} disabled={disabled} onChange={(x) => upd(i, { model: x.toUpperCase() })} />
              <LabeledInput
                label="Ano"
                value={v.year}
                disabled={disabled}
                onChange={(x) => upd(i, { year: x.replace(/\D/g, "").slice(0, 4) })}
              />
              <LabeledInput label="Cor" value={v.color} disabled={disabled} onChange={(x) => upd(i, { color: x.toUpperCase() })} />
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-[2fr_1fr]">
              <PlanInput
                value={v.plan}
                plans={plans}
                disabled={disabled}
                onChange={(x) => {
                  // Plano cadastrado escolhido e mensalidade vazia → preço do plano.
                  const p = plans?.find((pl) => pl.name === x);
                  upd(i, p && v.monthlyValue == null ? { plan: x, monthlyValue: p.value } : { plan: x });
                }}
              />
              <MoneyInput
                label="Mensalidade (R$)"
                value={v.monthlyValue ?? null}
                disabled={disabled}
                onChange={(n) => upd(i, { monthlyValue: n })}
              />
            </div>
            {planHasAssistance(v.plan, plans) && (
              <div className="mt-1.5">
                <AssistancePlanBadge plan={planKey(v.plan)} className="mr-1" />
                <span className="text-[11px] text-muted-foreground">este veículo passa a ter assistência ao salvar</span>
              </div>
            )}
          </div>
        ))
      )}

      {value.length > 0 && vehiclesMonthlyTotal(value) != null && (
        <div className="flex justify-end text-sm">
          <span className="text-muted-foreground">Total mensal:&nbsp;</span>
          <span className="font-semibold">{brl(vehiclesMonthlyTotal(value)!)}</span>
        </div>
      )}

      {!disabled && (
        <div className="flex flex-wrap gap-2">
          {registered.length > 0 && (
            <Button type="button" size="sm" variant="outline" onClick={() => setPicking((p) => !p)}>
              <Car className="mr-1.5 h-3.5 w-3.5" /> Escolher veículo cadastrado
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onChange([...value, blank(defaultPlan, defaultMonthlyValue)])}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Digitar veículo
          </Button>
        </div>
      )}

      {picking && !disabled && (
        <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-background">
          {registered.map((d) => {
            const inList = alreadyIn(d);
            return (
              <button
                key={d.id}
                type="button"
                disabled={inList || loadingId !== null}
                onClick={() => void pickRegistered(d)}
                className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted disabled:opacity-50"
              >
                <span className="min-w-0 truncate">
                  <span className="font-medium">{d.name}</span>
                  {plateOf(d) && <span className="ml-2 font-mono text-xs text-muted-foreground">{plateOf(d)}</span>}
                </span>
                {loadingId === d.id ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                ) : inList ? (
                  <span className="shrink-0 text-[11px] text-muted-foreground">já no contrato</span>
                ) : (
                  <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="h-9" />
    </label>
  );
}

// Plano: texto livre com sugestões dos Planos cadastrados.
function PlanInput({
  value,
  plans,
  onChange,
  disabled,
}: {
  value: string;
  plans?: Plan[];
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const listId = "contract-plan-options";
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">Plano</span>
      <Input value={value} disabled={disabled} list={plans?.length ? listId : undefined} onChange={(e) => onChange(e.target.value)} className="h-9" />
      {plans?.length ? (
        <datalist id={listId}>
          {plans.map((p) => (
            <option key={p.id} value={p.name} />
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

// Valor em reais digitado livre ("69,90"); vazio = sem valor.
function MoneyInput({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}) {
  const fmt = (n: number | null) => (n == null ? "" : n.toFixed(2).replace(".", ","));
  const [text, setText] = useState(fmt(value));
  useEffect(() => {
    const parsed = parse(text);
    if (parsed !== value) setText(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  function parse(t: string): number | null {
    const v = t.trim();
    if (!v) return null;
    const n = Number(v.replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", "."));
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input
        value={text}
        inputMode="decimal"
        placeholder="0,00"
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parse(e.target.value));
        }}
        onBlur={() => setText(fmt(parse(text)))}
        className="h-9"
      />
    </label>
  );
}
